/** @jsx jsx */
import { css, jsx, SerializedStyles } from '@emotion/core';
import { Grid, Typography, useTheme, useMediaQuery } from '@material-ui/core';
import { TypographyVariant } from '@material-ui/core/styles';

import { ICustomTheme } from 'interfaces';

const cssContainer =
  (isHighlighted: boolean, height: number) =>
  (theme: ICustomTheme): SerializedStyles =>
    css({
      background: isHighlighted
        ? theme.colors.paletteFromBO.main
        : theme.palette.grey[200],
      color: isHighlighted ? theme.colors.white : theme.colors.grey.main,
      flexWrap: 'nowrap',
      height,
      overflow: 'hidden',
    });

const cssLeft =
  (isHighlighted: boolean, isLarge: boolean, slantDeg: number) =>
  (theme: ICustomTheme): SerializedStyles =>
    css({
      alignItems: 'center',
      background: isHighlighted
        ? theme.colors.paletteFromBO.secondary
        : theme.colors.grey.main,
      color: theme.colors.white,
      display: 'flex',
      justifyContent: 'center',
      height: '100%',
      margin: theme.spacing(0, isLarge ? -4 : -2.5),
      padding: isLarge ? theme.spacing(4, 6, 4, 9) : theme.spacing(2, 4, 2, 6),
      transform: `skew(-${slantDeg}deg)`,
    });

const cssLeftValue = (slantDeg: number) => ({
  transform: `skew(${slantDeg}deg)`,
});

const cssRight =
  (isLarge: boolean, hasSamePadding: boolean) =>
  (theme: ICustomTheme): SerializedStyles =>
    css({
      alignItems: 'center',
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
      padding: isLarge
        ? theme.spacing(0, hasSamePadding ? 8 : 4, 0, 8)
        : theme.spacing(0, hasSamePadding ? 5 : 2, 0, 5),
    });

type SlantedBannerProps = {
  className?: string;
  isLeftHidden?: boolean;
  isHighlighted?: boolean;
  isLarge?: boolean;
  leftText: string | JSX.Element;
  rightText: string | JSX.Element;
};

type Sizes = {
  [key: string]: {
    [key: string]: {
      [key: string]: TypographyVariant;
    };
  };
};

const textSizes: Sizes = {
  left: {
    large: {
      up: 'h3',
      xs: 'h4',
    },
    normal: {
      up: 'h4',
      xs: 'h5',
    },
  },
  right: {
    large: {
      up: 'h2',
      xs: 'h4',
    },
    normal: {
      up: 'h4',
      xs: 'h5',
    },
  },
};

const heights = {
  large: {
    up: [105, 21],
    xs: [76, 28],
  },
  normal: {
    up: [76, 28],
    xs: [43, 43],
  },
};

const SlantedBanner = ({
  className = '',
  isLeftHidden = false,
  isHighlighted = false,
  isLarge = false,
  leftText,
  rightText,
}: SlantedBannerProps): JSX.Element => {
  const containerType = isLarge ? 'large' : 'normal';
  const theme: ICustomTheme = useTheme();
  const screenSize = useMediaQuery(theme.breakpoints.down('xs')) ? 'xs' : 'up';
  const left: TypographyVariant = textSizes.left[containerType][screenSize];
  const right: TypographyVariant = textSizes.right[containerType][screenSize];
  const [height, slantDeg] = heights[containerType][screenSize];

  return (
    <Grid
      container
      alignItems="center"
      className={className}
      css={cssContainer(isHighlighted, height)}
    >
      {isLeftHidden || (
        <Grid item css={cssLeft(isHighlighted, isLarge, slantDeg)}>
          <Typography
            color="inherit"
            variant={left}
            css={cssLeftValue(slantDeg)}
          >
            {leftText}
          </Typography>
        </Grid>
      )}

      <Grid item xs css={cssRight(isLarge, isLeftHidden)}>
        <Typography align="center" variant={right} css={{ lineHeight: 1 }}>
          {rightText}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default SlantedBanner;
